import React from 'react';
import { useTranslation } from '../context/TranslationContext';
import './ProductSelectionModal.css';

interface Product {
  product_name: string;
  product_brand: string;
  product_description: string;
  quantity: number;
  unit: string;
  unit_price: number;
  total_price: number;
  currency?: string;
}

interface ProductSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  products: Product[];
  onProductSelect: (product: Product) => void;
}

const ProductSelectionModal: React.FC<ProductSelectionModalProps> = ({
  isOpen,
  onClose,
  products,
  onProductSelect
}) => {
  const { t } = useTranslation();
  
  // Function to get currency symbol
  const getCurrencySymbol = (currency?: string) => {
    switch (currency) {
      case 'EUR':
        return '€';
      case 'USD':
        return '$';
      case 'GBP':
        return '£';
      case 'TL':
        return '₺';
      case 'CHF':
        return 'CHF';
      default:
        return '€'; // Default fallback
    }
  };

  // Function to get localized unit
  const getLocalizedUnit = (unit: string) => {
    switch (unit) {
      case 'piece':
        return t('common.piece');
      case 'kg':
        return t('common.kilogram');
      case 'm':
        return t('common.meter');
      case 'liter':
        return t('common.liter');
      case 'box':
        return t('common.box');
      case 'set':
        return t('common.set');
      case 'hour':
        return t('common.hour');
      case 'day':
        return t('common.day');
      default:
        return unit;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="product-selection-overlay" onClick={onClose}>
      <div className="product-selection-modal" onClick={(e) => e.stopPropagation()}>
        <div className="product-selection-header">
          <h2>{t('common.multiple_products_found')}</h2>
          <button className="close-button" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <div className="product-selection-content">
          <p className="product-selection-description">
            {t('common.select_product_description')}
          </p>

          <div className="products-grid">
            {products.map((product, index) => (
              <div 
                key={index} 
                className="product-card"
                onClick={() => onProductSelect(product)}
              >
                <div className="product-header">
                  <h3 className="product-name">{product.product_name}</h3>
                  {product.product_brand && (
                    <span className="product-brand">{product.product_brand}</span>
                  )}
                </div>

                <div className="product-details">
                  <div className="product-info">
                    <span className="product-quantity">
                      {t('common.quantity')}: {product.quantity} {getLocalizedUnit(product.unit)}
                    </span>
                    <span className="product-price">
                      {t('common.unit_price')}: {getCurrencySymbol(product.currency)}{product.unit_price?.toFixed(2) || 'N/A'}
                    </span>
                    <span className="product-total">
                      {t('common.total_price')}: {getCurrencySymbol(product.currency)}{product.total_price?.toFixed(2) || 'N/A'}
                    </span>
                  </div>

                  {product.product_description && (
                    <p className="product-description">
                      {product.product_description}
                    </p>
                  )}
                </div>

                <div className="product-select-button">
                  <span>{t('common.select_this_product')}</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12H19M12 5L19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductSelectionModal; 